import { Icon } from "@circle-react-shared/Icon";
import { omit } from "lodash";
import type {
  ChangeEventHandler,
  ComponentPropsWithoutRef,
  ReactNode,
} from "react";
import { useEffect, useRef } from "react";

import { TippyV2 } from "@/react/components/SettingsApp/CompassCompat/Tooltip";
import {
  anchorClassNames,
  legacyClassNames,
} from "@/react/helpers/cssAnchorHelpers";
import { classNames } from "@/react/helpers/twMergeWithCN";

export interface Option {
  description?: string | ReactNode;
  label: ReactNode;
  richLabel?: ReactNode;
  value?: string | number;
}

export interface CheckboxItemProps extends ComponentPropsWithoutRef<"input"> {
  checked: boolean;
  className?: string;
  dataTestId?: string;
  error?: ReactNode;
  /** Sets the indeterminate state on checkbox. This only changes the visual of checkbox, it remains in the checked or unchecked state as provided by checked prop */
  indeterminate?: boolean;
  inputClassName?: string;
  invalid?: boolean;
  isDescriptionTooltip?: boolean;
  /** Adds extra gap and margin to the component */
  isFormItem?: boolean;
  isRoundedFull?: boolean;
  labelClassName?: string;
  layout?: "horizontal" | "vertical";
  name?: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
  option: Option;
  tooltipClassName?: string;
  /** Cannot uncheck component if true */
  uncheckDisabled?: boolean;
}

export const CheckboxItem = ({
  checked,
  error,
  isDescriptionTooltip = false,
  isFormItem = true,
  isRoundedFull = false,
  invalid = false,
  className,
  labelClassName,
  inputClassName,
  tooltipClassName,
  name,
  onChange,
  option,
  uncheckDisabled = false,
  indeterminate = false,
  ...rest
}: CheckboxItemProps) => {
  const checkboxRef = useRef<HTMLInputElement>(null);
  const { label, richLabel, description, value } = option;
  const id = rest.id || `${name}-${value}`;

  useEffect(() => {
    const checkboxEl = checkboxRef.current;
    if (checkboxEl) {
      checkboxEl.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  const inputProps = omit(rest, ["transformValue", "layout"]);

  return (
    <div className={classNames(isFormItem ? "mt-4" : undefined, className)}>
      <div className="flex">
        <div
          className={classNames("inline-flex items-start", {
            "gap-3": isFormItem,
          })}
        >
          <div className="flex items-center">
            <input
              ref={checkboxRef}
              className={classNames(
                "text-light bg-primary hover:bg-tertiary hover:border-secondary checked:disabled:bg-tertiary disabled:bg-tertiary checked:disabled:border-selector-inactive disabled:border-selector-inactive border-secondary focus:bg-tertiary focus:border-secondary focus:outline-secondary size-4 cursor-pointer rounded-sm focus:outline-2 focus:ring-0 focus:ring-offset-0",
                {
                  "mt-[3px]": isFormItem,
                  "rounded-full": isRoundedFull,
                  "border-red-500": invalid,
                },
                inputClassName,
              )}
              type="checkbox"
              checked={checked}
              onChange={e => {
                if (e.target.checked || !uncheckDisabled) onChange(e);
              }}
              id={id}
              name={name}
              {...inputProps}
            />
          </div>
          <div>
            <div className="flex items-center">
              <label
                className={classNames(
                  "text-dark mb-0 w-full cursor-pointer",
                  { "cursor-not-allowed opacity-20": inputProps.disabled },
                  labelClassName,
                )}
                htmlFor={id}
              >
                {richLabel || label}
              </label>
              {isDescriptionTooltip && (
                <TippyV2 content={description}>
                  <span className="flex">
                    <Icon
                      type="question-mark"
                      className={classNames(
                        anchorClassNames("icon"),
                        legacyClassNames("icon--xs"),
                        "text-dark ml-2",
                        {
                          "cursor-not-allowed opacity-20": inputProps.disabled,
                        },
                        tooltipClassName,
                      )}
                    />
                  </span>
                </TippyV2>
              )}
            </div>
            {!isDescriptionTooltip && (
              <p className="text-default mt-1 text-xs">{description}</p>
            )}
          </div>
        </div>
      </div>
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
};
